import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AttributeProof, PaymentTermsInput, RecordRefund, RetailerPaymentTerms, PurchaseInvoiceDetail, SetCartLine, SetCartLines,
  SubmitOrder,
} from '@miraki/contracts';
import { api, getPortal } from './client';
import {
  invalidateAll, orderDetailWrite, portalKey, consignmentInvoiceCreateWrite, consignmentInvoiceWrite,
  settleWrite,
} from './write-keys';
import { CART_WRITE_SCOPE, cartWrites } from './cart-order';
import { useSession } from '../auth/session';
import type {
  Application, AuditEntry, Cart, Country, InternalUser, RetailerAddress, RetailerUser,
  LastOrder, OrderDetail, OrderSummary, ReorderResult, RetailerOrderSummary, PackingList, PageDef, PaymentProof, PendingPayment, PermissionDef,
  ConsignmentInvoiceCandidate, ConsignmentInvoiceDetail, ConsignmentInvoiceRow, ShopConsignmentInvoice, Reconciliation, ShopConsignmentInvoiceRow,
  RetailerDetail, RetailerRow, Role, Shipment, ShipmentDetail,
  OrderMilestones, RefundResult, OrderChanges, AddressRequestRow,
  KycDocument, KycPack, TradeReference,
} from './types';

/**
 * The portal is part of every cache key, read from the session rather than from
 * module state.
 *
 * The module-state version was wrong in a way only a browser could show: the
 * effect that publishes the new portal runs *after* the render that computes the
 * key, so switching Global to Australia recomputed the key from the previous
 * value, hit the same cache entry, and displayed Global's retailers under the
 * Australia label. That is the SC-02 boundary leaking through the client — the
 * exact failure the key exists to prevent.
 */
function useScope() {
  const { portal } = useSession();
  return (key: unknown[]) => portalKey(portal?.code ?? null, key);
}

/** The portal this hook's keys are scoped to, for the write-key sets in `write-keys.ts`. */
function usePortalCode(): string | null {
  const { portal } = useSession();
  return portal?.code ?? null;
}

export const useCountries = () =>
  useQuery({ queryKey: ['countries'], queryFn: () => api.get<Country[]>('/countries') });

export const usePermissions = () =>
  useQuery({ queryKey: ['permissions'], queryFn: () => api.get<PermissionDef[]>('/permissions') });

export const usePages = () =>
  useQuery({ queryKey: ['pages'], queryFn: () => api.get<PageDef[]>('/pages') });

export const useRoles = () =>
  useQuery({ queryKey: ['roles'], queryFn: () => api.get<Role[]>('/roles') });

export const useUsers = () =>
  useQuery({ queryKey: ['users'], queryFn: () => api.get<InternalUser[]>('/users') });

export const useApplications = () => {
  const scoped = useScope();
  return useQuery({ queryKey: scoped(['applications']), queryFn: () => api.get<Application[]>('/applications') });
};

/**
 * ON-05 — what the acting portal gives an account approved with the setup boxes left blank.
 *
 * A-02 shows these beside the boxes, so "leave blank" is a visible decision rather than a guess.
 * Portal-scoped like everything else, so switching portal re-reads it (`useScope`).
 */
export interface AccountSetupDefaults {
  portalCode: string;
  currency: string;
  /** BG-01 — null means this portal lends nothing by default, so a budget is required. */
  defaultBudget: string | null;
  /** BG-04 — a decimal percentage string, already resolved through the portal's settings. */
  budgetTolerancePct: string;
}
export const useAccountSetupDefaults = () => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['applications', 'account-setup-defaults']),
    queryFn: () => api.get<AccountSetupDefaults>('/applications/account-setup-defaults'),
  });
};

/** A-01's "Payments to verify" tile + table. */
export const usePendingPayments = (enabled = true) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['payments', 'pending']),
    queryFn: () => api.get<PendingPayment[]>('/payments?status=PENDING'),
    // The route needs `payment.verify`; a caller who lacks it passes false rather than collect a 403.
    enabled,
  });
};

/** A-01's "Queries unanswered" tile — a count, not a list. */
export const useOpenQueryCount = () => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['orders', 'queries', 'open-count']),
    queryFn: () => api.get<{ count: number }>('/orders/queries/open-count'),
  });
};

export const useRetailers = (enabled = true) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['retailers']),
    queryFn: () => api.get<RetailerRow[]>('/retailers'),
    // The create-login dialog opened from one account already knows which; it should not
    // fetch every account on the portal to populate a picker it will not show.
    enabled,
  });
};

export const useRetailer = (id: string | undefined) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['retailer', id]),
    queryFn: () => api.get<RetailerDetail>(`/retailers/${id}`),
    enabled: !!id,
  });
};

export const useAudit = (
  params: {
    objectId?: string | undefined; action?: string | undefined; limit?: number | undefined;
    /** AU-04 — free text, matched on the server across actor, action, object and reason. */
    q?: string | undefined;
    from?: string | undefined; to?: string | undefined;
  } = {},
) => {
  const qs = new URLSearchParams();
  if (params.objectId) qs.set('objectId', params.objectId);
  if (params.action) qs.set('action', params.action);
  if (params.q) qs.set('q', params.q);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  qs.set('limit', String(params.limit ?? 50));
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['audit', qs.toString()]),
    queryFn: () => api.get<AuditEntry[]>(`/audit?${qs}`),
  });
};

/**
 * Every mutation invalidates the audit log alongside its own resource: a change
 * that does not show up in the log on the next screen looks like it was not
 * recorded, and the whole point of writing audit from Sprint 2 is that it is.
 */
function useInvalidating<TArgs, TResult>(
  fn: (args: TArgs) => Promise<TResult>,
  makeKeys: (scoped: (key: unknown[]) => unknown[]) => unknown[][],
) {
  const qc = useQueryClient();
  const scoped = useScope();
  const keys = () => makeKeys(scoped);
  return useMutation({
    mutationFn: fn,
    onSuccess: () => invalidateAll(qc, [...keys(), scoped(['audit'])]),
  });
}

export const useDecideApplication = () =>
  useInvalidating(
    ({ id, decision, body }: { id: string; decision: 'approve' | 'reject' | 'request-information'; body: unknown }) =>
      api.post<unknown>(`/applications/${id}/${decision}`, body),
    (scoped) => [scoped(['applications']), scoped(['retailers'])],
  );


/* ------------------------------------------- KYC documents and trade references (ON-02a, D94) */

/**
 * What a write to an application's KYC pack re-reads.
 *
 * The documents themselves, the queue (whose rows carry `documentCount`, and which is what the
 * "no documents" refusal on approval is read against), and the audit log that `useInvalidating`
 * adds. Named once so the add and the remove cannot disagree about it.
 */
const APPLICATION_DOCUMENT_KEYS = (scoped: (key: unknown[]) => unknown[]) => [
  scoped(['applicationDocuments']),
  scoped(['applications']),
];

/** A-02 — what is attached to the application being decided. Metadata only. */
export const useApplicationDocuments = (applicationId: string | undefined) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['applicationDocuments', applicationId ?? '']),
    queryFn: () => api.get<KycDocument[]>(`/applications/${applicationId}/documents`),
    enabled: !!applicationId,
  });
};

/** ON-02 — who the applicant says will vouch for them. The point is that somebody rings them. */
export const useApplicationTradeReferences = (applicationId: string | undefined) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['applicationTradeReferences', applicationId ?? '']),
    queryFn: () => api.get<TradeReference[]>(`/applications/${applicationId}/trade-references`),
    enabled: !!applicationId,
  });
};

/**
 * CR-57 P3 / D105 — Admin approves or rejects a retailer's KYC pack on A-04. Approving takes no body;
 * rejecting takes the reason the retailer will read on R-18. A decision changes what A-04 shows (the
 * status, who decided, the reason) and the pack read, so both are re-read.
 */
export const useDecideRetailerKyc = (retailerId: string) => {
  const qc = useQueryClient();
  const scoped = useScope();
  return useMutation({
    mutationFn: ({ decision, reason }: { decision: 'approve' | 'reject'; reason?: string }) =>
      api.post<{ kycStatus: string }>(`/retailers/${retailerId}/kyc/${decision}`, decision === 'reject' ? { reason } : undefined),
    onSuccess: () => invalidateAll(qc, [
      scoped(['retailer', retailerId]),
      scoped(['retailerKycDocuments', retailerId]),
    ]),
  });
};

/** A-04 Details — the same pack, read through `onboardedFrom` rather than copied at approval. */
export const useRetailerKycDocuments = (retailerId: string | undefined) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['retailerKycDocuments', retailerId ?? '']),
    queryFn: () => api.get<KycDocument[]>(`/retailers/${retailerId}/kyc-documents`),
    enabled: !!retailerId,
  });
};

export const useRetailerTradeReferences = (retailerId: string | undefined) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['retailerTradeReferences', retailerId ?? '']),
    queryFn: () => api.get<TradeReference[]>(`/retailers/${retailerId}/trade-references`),
    enabled: !!retailerId,
  });
};

/**
 * ON-02a — Admin files a document on the applicant's behalf, which is how a chase finishes.
 *
 * Raw bytes on the body with the name in the query string, so it does not go through the JSON
 * client and the portal header is added by hand — the same shape `useAddCertificateAttachment`
 * uses. Through `useInvalidating`, so the re-read is the capped `invalidateAll` like every other
 * write on this screen.
 */
export const useAddApplicationDocument = (applicationId: string) =>
  useInvalidating(
    async (file: File) => {
      const portal = getPortal();
      const res = await fetch(
        `/api/applications/${applicationId}/documents?${new URLSearchParams({ name: file.name })}`,
        {
          method: 'POST',
          credentials: 'same-origin',
          /*
           * The browser's own guess at the type, which the server does not trust and does not
           * store — it reads the bytes. It is sent because express decides from it whether to
           * hand the handler a Buffer at all; a file with no type at all is sent as a PDF-
           * neutral octet stream and refused, which is the honest answer.
           */
          headers: { 'content-type': file.type || 'application/octet-stream', ...(portal ? { 'x-portal': portal } : {}) },
          body: file,
        },
      );
      if (!res.ok) throw await uploadFailure(res);
      return res.json() as Promise<{ documentCount: number; remaining: number }>;
    },
    APPLICATION_DOCUMENT_KEYS,
  );

export const useRemoveApplicationDocument = (applicationId: string) =>
  useInvalidating(
    (documentId: string) =>
      api.del<unknown>(`/applications/${applicationId}/documents/${documentId}`),
    APPLICATION_DOCUMENT_KEYS,
  );

/** The server's own sentence, or a status code if it did not send one. */
export async function uploadFailure(res: Response): Promise<Error> {
  const text = await res.text();
  let message = `Upload failed (${res.status})`;
  try { message = (JSON.parse(text) as { message?: string }).message ?? message; } catch { /* not json */ }
  return new Error(message);
}

/**
 * Download one KYC document.
 *
 * A plain link cannot carry the `x-portal` header the route scopes on, so the bytes are fetched
 * and handed to the browser as a blob. **Saved rather than opened in a tab**, because the server
 * sends `Content-Disposition: attachment` for these: the file came from an anonymous uploader and
 * rendering it inside the portal's own origin is a hazard the download does not need to take.
 */
export async function downloadKycDocument(doc: KycDocument): Promise<void> {
  const portal = getPortal();
  const res = await fetch(`/api/applications/documents/${doc.id}/file`, {
    credentials: 'same-origin',
    ...(portal ? { headers: { 'x-portal': portal } } : {}),
  });
  if (!res.ok) throw await uploadFailure(res);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = doc.fileName;
  a.click();
  // The browser has taken its own reference by now; release ours after it has.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export const useSetTerms = () =>
  useInvalidating(
    ({ id, body }: { id: string; body: unknown }) => api.post<unknown>(`/retailers/${id}/terms`, body),
    (scoped) => [scoped(['retailers']), scoped(['retailer'])],
  );

/** Admin: a retailer's payment terms — effective, pending revision, history. */
export const useRetailerPaymentTerms = (id: string | undefined) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['retailer', id, 'payment-terms']),
    queryFn: () => api.get<RetailerPaymentTerms>(`/retailers/${id}/payment-terms`),
    enabled: !!id,
  });
};

export const useSetPaymentTerms = () =>
  useInvalidating(
    ({ id, body }: { id: string; body: PaymentTermsInput }) =>
      api.put<RetailerPaymentTerms>(`/retailers/${id}/payment-terms`, body),
    (scoped) => [scoped(['retailer'])],
  );

/** Retailer: own effective terms (also readable while the portal is locked for acceptance). */
export const useMyPaymentTerms = () =>
  useQuery({
    queryKey: ['shop', 'payment-terms'],
    queryFn: () => api.get<RetailerPaymentTerms>('/shop/payment-terms'),
  });

export const useSetRetailerStatus = () =>
  useInvalidating(
    ({ id, body }: { id: string; body: unknown }) => api.post<unknown>(`/retailers/${id}/status`, body),
    (scoped) => [scoped(['retailers']), scoped(['retailer'])],
  );

export const useSetUserActive = () =>
  useInvalidating(
    ({ id, active, reason }: { id: string; active: boolean; reason?: string }) =>
      api.post<unknown>(`/users/${id}/${active ? 'reactivate' : 'suspend'}`, active ? undefined : { reason }),
    (_scoped) => [['users']],
  );

/**
 * A-06 and A-07 write paths.
 *
 * Roles and users are not portal-scoped — a role reaches portals rather than
 * belonging to one (RL-06) — so their cache keys stay unscoped, unlike retailers.
 * Anything touching a role invalidates `users` too: a user's row shows the role
 * names it holds, so renaming a role leaves that list stale otherwise.
 */
const ACCESS_KEYS = [['users'], ['roles']];

export const useCreateUser = () =>
  useInvalidating((body: unknown) => api.post<unknown>('/users', body), () => ACCESS_KEYS);

export const useUpdateUser = () =>
  useInvalidating(
    ({ id, body }: { id: string; body: unknown }) => api.put<unknown>(`/users/${id}`, body),
    () => ACCESS_KEYS,
  );

export const useSetUserPassword = () =>
  useInvalidating(
    ({ id, password }: { id: string; password: string }) =>
      api.put<unknown>(`/users/${id}/password`, { password }),
    () => ACCESS_KEYS,
  );

export const useSetUserRoles = () =>
  useInvalidating(
    ({ id, roleIds }: { id: string; roleIds: string[] }) =>
      api.put<unknown>(`/users/${id}/roles`, { roleIds }),
    () => ACCESS_KEYS,
  );

/*
 * A-06's retailer half.
 *
 * `retailerUsers` is PORTAL-SCOPED where `users` is not: an internal user belongs to Miraki
 * and a role reaches portals (RL-06), but a trade customer's login belongs to a retailer,
 * which belongs to exactly one portal. Caching it unscoped would show the GCC list to an
 * administrator who has just switched to AUS until something happened to evict it.
 */
const RETAILER_USER_KEYS = (scoped: (k: unknown[]) => unknown[]) => [scoped(['retailerUsers'])];

/**
 * Every trade login on the acting portal, or just one account's when `retailerId` is given.
 *
 * One endpoint, two callers: A-06's merged list asks for all of them, A-04's Users panel asks
 * for one account's. A separate route per caller would be two answers to one question, and
 * they would eventually disagree.
 */
export const useRetailerUsers = (enabled = true, retailerId?: string) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['retailerUsers', retailerId ?? 'all']),
    queryFn: () => api.get<RetailerUser[]>(
      retailerId ? `/retailer-users?retailerId=${encodeURIComponent(retailerId)}` : '/retailer-users',
    ),
    // Only asked for when the signed-in user holds `retailer_user.manage`. Without the
    // guard the screen would fire a request it knows will 403 on every visit by somebody
    // who only administers staff, and show them an error for a half of the page they were
    // never offered.
    enabled,
  });
};

/*
 * ON-08 addresses. Portal-scoped like everything hanging off a retailer, and keyed by the
 * account so two accounts open in two tabs do not share one cached list.
 *
 * Every write invalidates `retailer` as well: the detail header shows the account's city,
 * and a registered address is where that will come from.
 */
const ADDRESS_KEYS = (scoped: (k: unknown[]) => unknown[]) =>
  [scoped(['retailerAddresses']), scoped(['retailer']), scoped(['retailers'])];

export const useSetRetailerCompany = (retailerId: string) =>
  useInvalidating(
    (body: unknown) => api.put<unknown>(`/retailers/${retailerId}/company`, body),
    // `retailers` too: the list shows the name and place, and a rename there would otherwise
    // keep showing the old one until something else evicted it.
    (scoped) => [scoped(['retailer']), scoped(['retailers']), scoped(['audit'])],
  );

export const useRetailerAddresses = (retailerId: string | undefined) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['retailerAddresses', retailerId ?? '']),
    queryFn: () => api.get<RetailerAddress[]>(`/retailers/${retailerId}/addresses`),
    enabled: !!retailerId,
  });
};

export const useAddRetailerAddress = (retailerId: string) =>
  useInvalidating(
    (body: unknown) => api.post<unknown>(`/retailers/${retailerId}/addresses`, body),
    ADDRESS_KEYS,
  );

export const useUpdateRetailerAddress = (retailerId: string) =>
  useInvalidating(
    ({ id, body }: { id: string; body: unknown }) =>
      api.put<unknown>(`/retailers/${retailerId}/addresses/${id}`, body),
    ADDRESS_KEYS,
  );

export const useRemoveRetailerAddress = (retailerId: string) =>
  useInvalidating(
    ({ id }: { id: string }) => api.del<unknown>(`/retailers/${retailerId}/addresses/${id}`),
    ADDRESS_KEYS,
  );

/**
 * The roles a user administrator may assign. Names only — `/roles` carries each role's
 * permissions and screens and is behind `role.manage`, which Operations Admin deliberately
 * does not hold, so asking for it rendered A-06's Roles section empty for the very person
 * meant to use it.
 */
export const useAssignableRoles = () =>
  useQuery({
    queryKey: ['assignableRoles'],
    queryFn: () => api.get<Role[]>('/roles/assignable'),
  });

export const useCreateRetailerUser = () =>
  useInvalidating(
    (body: unknown) => api.post<unknown>('/retailer-users', body),
    // Also invalidates `retailers`: the account list and detail both show how many logins an
    // account has, and a new one leaves those stale otherwise.
    (scoped) => [scoped(['retailerUsers']), scoped(['retailers']), scoped(['retailer'])],
  );

export const useUpdateRetailerUser = () =>
  useInvalidating(
    ({ id, body }: { id: string; body: unknown }) => api.put<unknown>(`/retailer-users/${id}`, body),
    RETAILER_USER_KEYS,
  );

export const useSetRetailerUserPassword = () =>
  useInvalidating(
    ({ id, password }: { id: string; password: string }) =>
      api.put<unknown>(`/retailer-users/${id}/password`, { password }),
    RETAILER_USER_KEYS,
  );

export const useSetRetailerUserRoles = () =>
  useInvalidating(
    ({ id, roleIds }: { id: string; roleIds: string[] }) =>
      api.put<unknown>(`/retailer-users/${id}/roles`, { roleIds }),
    RETAILER_USER_KEYS,
  );

export const useSetRetailerUserActive = () =>
  useInvalidating(
    ({ id, active, reason }: { id: string; active: boolean; reason?: string }) =>
      api.post<unknown>(`/retailer-users/${id}/${active ? 'reactivate' : 'suspend'}`,
        active ? undefined : { reason }),
    RETAILER_USER_KEYS,
  );

export const useCreateRole = () =>
  useInvalidating((body: unknown) => api.post<unknown>('/roles', body), () => ACCESS_KEYS);

export const useUpdateRole = () =>
  useInvalidating(
    ({ id, body }: { id: string; body: unknown }) => api.put<unknown>(`/roles/${id}`, body),
    () => ACCESS_KEYS,
  );

export const useSetRolePermissions = () =>
  useInvalidating(
    ({ id, permissions }: { id: string; permissions: string[] }) =>
      api.put<unknown>(`/roles/${id}/permissions`, { permissions }),
    () => ACCESS_KEYS,
  );

export const useSetRolePages = () =>
  useInvalidating(
    ({ id, pageIds }: { id: string; pageIds: string[] }) =>
      api.put<unknown>(`/roles/${id}/pages`, { pageIds }),
    () => ACCESS_KEYS,
  );

export const useSetRolePortals = () =>
  useInvalidating(
    ({ id, portalIds }: { id: string; portalIds: string[] }) =>
      api.put<unknown>(`/roles/${id}/portals`, { portalIds }),
    () => ACCESS_KEYS,
  );

export const useDeleteRole = () =>
  useInvalidating(
    ({ id, reason }: { id: string; reason?: string }) =>
      api.del<unknown>(`/roles/${id}`, reason ? { reason } : {}),
    () => ACCESS_KEYS,
  );

// -------------------------------------------------------------------- R-04/R-05 cart
//
// Retailer-side, not portal-scoped by `useScope` above: a retailer holds exactly
// one portal (no switcher — `auth/session.tsx`), and `Shop.tsx`'s existing R-02
// screens already use the plain `['shop', ...]` key shape rather than the admin
// `useScope` pattern. These hooks match that, so `Shop.tsx`'s add-to-cart control
// and `screens/shop/Order.tsx` share one mutation instead of duplicating it.

const CART_KEY = ['shop', 'cart'];

/** GET /shop/cart. Recomputed on every read — drops now-unpublished lines,
 *  reprices from the live `listPrice` (invariant 6/7, implementation spec §2).
 *  `enabled` lets a caller skip the request entirely for a sign-in that holds
 *  neither `shop.order` nor `shop.order_view` — the route would only 403. */
export const useCart = (opts: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: CART_KEY,
    queryFn: () => api.get<Cart>('/shop/cart'),
    enabled: opts.enabled ?? true,
  });

/**
 * PUT /shop/cart/lines — `zSetCartLine` in `@miraki/contracts` (frozen).
 * Quantity 0 deletes the line; no minimum (D4, CR-07) — any quantity from 1
 * upward is valid, there is no MOQ to enforce.
 *
 * The body is typed inline rather than imported from `@miraki/contracts`,
 * matching the convention `api/catalog.ts` already follows for request
 * bodies (ground-truth §6: only the route validates against the contract).
 * That convention turns out not to be optional here — `@miraki/contracts`
 * ships CommonJS (`exports.zSetCartLine = ...`, no `"exports"` map picking an
 * ESM build for the browser), and a *value* import of it was never proven to
 * work from `apps/web` before this session. `@miraki/money`, built the same
 * way, was checked directly: Vite does not pre-bundle either workspace
 * package (absent from `node_modules/.vite/deps/_metadata.json`), so a
 * request resolves straight through `/@fs/` to the raw CJS file, which has no
 * `export` statement for a browser's native ESM loader to find. A type-only
 * import would still be safe (erased before the browser ever sees it), but
 * there is no need for one here.
 */
export const useSetCartLine = () => {
  const qc = useQueryClient();
  return useMutation({
    // One cart write at a time, and no stale answer over a newer one — `cart-order.ts` says why.
    scope: { id: CART_WRITE_SCOPE },
    mutationFn: (body: SetCartLine) => api.put<Cart>('/shop/cart/lines', body),
    onMutate: () => ({ ticket: cartWrites.begin() }),
    onSuccess: (cart, _body, { ticket }) => {
      if (cartWrites.accept(ticket)) qc.setQueryData(CART_KEY, cart);
    },
  });
};

/**
 * R-02's several-at-once table. One request for every row, not one per row:
 * each cart write re-reads the whole cart over the network, so six rows sent
 * separately is six waits where this is one — and the cart it returns is the
 * cart after all of them, so there is a single, consistent update rather than
 * six intermediate ones flickering through the badge.
 */
export const useSetCartLines = () => {
  const qc = useQueryClient();
  return useMutation({
    // The same scope and the same ticket as the single-line write: the grid and a row's own
    // "Add to order" are writes to one cart and must not be allowed to overtake each other.
    scope: { id: CART_WRITE_SCOPE },
    mutationFn: (body: SetCartLines) => api.put<Cart>('/shop/cart/lines/bulk', body),
    onMutate: () => ({ ticket: cartWrites.begin() }),
    onSuccess: (cart, _body, { ticket }) => {
      if (cartWrites.accept(ticket)) qc.setQueryData(CART_KEY, cart);
    },
  });
};

/** SH-07 — the retailer acknowledges a shipment arrived. The server stamps DELIVERED; the
 *  orders list is refetched so the new state shows at once. Safe to repeat. */
export const useAcknowledgeReceipt = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shipmentId: string) =>
      api.post<{ alreadyDelivered?: boolean }>(`/shop/shipments/${shipmentId}/receipt`, {}),
    // Capped. `['shop', 'orders']` is the acting screen's OWN query, which is the case the
    // write-keys split does not narrow — and on a real account it is the slowest query the
    // retailer has. Observed on GenZ Jewels: the shipment was DELIVERED and the four pieces
    // HELD in the database while the dialog still read "Confirming…" fourteen seconds later.
    // Waiting for the screen's own answer is right; waiting without a bound is the defect.
    onSettled: () => invalidateAll(qc, [['shop', 'orders'], ['shop', 'consignment-invoices']]),
  });
};

/** R-06 — "Reorder": the order's pieces go into the cart at current prices. */
export const useReorder = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => api.post<ReorderResult>(`/shop/orders/${orderId}/reorder`),
    onSuccess: () => qc.invalidateQueries({ queryKey: CART_KEY }),
  });
};

/** CT-08 — "order again". Read-only; shape assumed pending the parallel
 *  session building the endpoint, see `api/types.ts`'s `LastOrder`. */
export const useLastOrder = () =>
  useQuery({ queryKey: ['shop', 'last-order'], queryFn: () => api.get<LastOrder>('/shop/last-order') });

/** DELETE /shop/cart — the pinned bar's "Clear". Empties every line at once. */
export const useClearCart = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<{ cleared: boolean }>('/shop/cart'),
    onSuccess: () => qc.invalidateQueries({ queryKey: CART_KEY }),
  });
};

// ------------------------------------------------------------- R-06/R-07/A-14/A-15 orders
//
// `OrderService`/`order.machine.ts` were already fully written before this
// session's `OrderController` gave them an HTTP surface (feasibility study,
// `docs/plans/2026-09-11-feasibility-retailer-lifecycle-e2e.md`) — these hooks
// are the wiring, not new business logic.

/** POST /shop/order/submit — the cart -> order bridge. `body.variantCodes`
 *  narrows it to a subset of the cart (partial checkout); omitted, it is the
 *  whole cart. Either way, only the lines that were actually submitted leave
 *  the cart server-side, so a plain cache invalidation (not an optimistic
 *  clear) is what has to run next.
 *
 *  Always sends a real (if empty) JSON body — `api.post` omits the body
 *  entirely, with no `content-type`, when it is `undefined`, and `zSubmitOrder`
 *  is a Zod object schema: it rejects a request with no body at all (there is
 *  nothing there to parse as `{}`), not just one with the wrong shape. `{}` is
 *  a body the schema accepts, since every field on it is optional. */
export const useSubmitOrder = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body?: SubmitOrder) => api.post<{ id: string; reference: string; status: string; total: string; currency: string }>(
      '/shop/order/submit', body ?? {},
    ),
    onSuccess: () => invalidateAll(qc, [CART_KEY, ['shop', 'orders']]),
  });
};

export const useMyOrders = () =>
  useQuery({ queryKey: ['shop', 'orders'], queryFn: () => api.get<RetailerOrderSummary[]>('/shop/orders') });

/** How often an open order page looks for new conversation messages (OR-14).
 *  Only while the tab is visible — react-query pauses it in the background. */
const ORDER_CHAT_POLL_MS = 15_000;

export const useMyOrder = (id: string | undefined) =>
  useQuery({
    queryKey: ['shop', 'orders', id],
    queryFn: () => api.get<OrderDetail>(`/shop/orders/${id}`),
    enabled: !!id,
    refetchInterval: ORDER_CHAT_POLL_MS,
  });

/** OR-14 — the retailer's side of an order's conversation. */
export const useSendMyOrderMessage = (orderId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => api.post<unknown>(`/shop/orders/${orderId}/messages`, { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shop', 'orders', orderId] }),
  });
};

const useMyOrderTransition = (action: 'accept' | 'decline') => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<unknown>(`/shop/orders/${id}/${action}`),
    onSuccess: (_data, id) => invalidateAll(qc, [['shop', 'orders'], ['shop', 'orders', id]]),
  });
};
export const useAcceptOrder = () => useMyOrderTransition('accept');

/** The retailer turns the offer down, optionally saying why. */
export const useDeclineOrder = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api.post<unknown>(`/shop/orders/${id}/decline`, { reason: reason?.trim() || undefined }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shop', 'orders'] }),
  });
};

/** The retailer's feedback on an offer — sends it back to Miraki to revise. */
export const useRequestRevision = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, feedback }: { id: string; feedback: string }) =>
      api.post<unknown>(`/shop/orders/${id}/request-revision`, { feedback }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shop', 'orders'] }),
  });
};

export const useRaiseQuery = (orderId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { subject: string; body: string }) =>
      api.post<unknown>(`/shop/orders/${orderId}/queries`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shop', 'orders', orderId] }),
  });
};

/** A-14/A-15 — the admin side. Portal-scoped like every other admin list. */
export const useOrders = () => {
  const scoped = useScope();
  return useQuery({ queryKey: scoped(['orders']), queryFn: () => api.get<OrderSummary[]>('/orders') });
};

export const useOrder = (id: string | undefined) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['orders', id]),
    queryFn: () => api.get<OrderDetail>(`/orders/${id}`),
    enabled: !!id,
    refetchInterval: ORDER_CHAT_POLL_MS,
  });
};

/** OR-14 — Miraki's side of an order's conversation. */
export const useSendOrderMessage = (orderId: string) => {
  const qc = useQueryClient();
  const scoped = useScope();
  return useMutation({
    mutationFn: (body: string) => api.post<unknown>(`/orders/${orderId}/messages`, { body }),
    onSuccess: () => invalidateAll(qc, [scoped(['orders', orderId]), scoped(['orders'])]),
  });
};

/**
 * A-14's Excel download — the detail (lines) of several orders at once, through
 * the same cache entries `useOrder` fills, so an order already opened is not
 * fetched again. A few at a time rather than all at once: `GET /orders` is
 * unpaged, so "all" can be every order on the portal.
 */
export const useFetchOrderDetails = () => {
  const qc = useQueryClient();
  const scoped = useScope();
  return async (ids: string[], onProgress?: (done: number) => void): Promise<Map<string, OrderDetail>> => {
    const out = new Map<string, OrderDetail>();
    const queue = [...ids];
    let done = 0;
    const worker = async () => {
      for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
        const orderId = id;
        out.set(orderId, await qc.fetchQuery({
          queryKey: scoped(['orders', orderId]),
          queryFn: () => api.get<OrderDetail>(`/orders/${orderId}`),
        }));
        onProgress?.(++done);
      }
    };
    await Promise.all(Array.from({ length: Math.min(4, ids.length) }, worker));
    return out;
  };
};

/**
 * A-15 — the admin's own move on an order (confirm, begin manufacturing, mark ready, reject…).
 *
 * `orderDetailWrite` rather than a list written out here, because the same transition route is
 * also driven from the manufacturing board: one statement of what the write changes, used by
 * both, or the screen you are not on keeps the old answer. Returned, not fired and forgotten,
 * so `mutateAsync` resolves once the new status is on screen instead of leaving the button
 * re-offering the action over the pre-write status.
 *
 * It awaits only THIS screen's half of that re-read. Awaiting all of it is what left "Confirm
 * order" and "Begin manufacturing" on "Working…" for ever over a write the database had already
 * taken — the three-way split, and the cap on it, are in `write-keys.ts`.
 */
export const useOrderTransition = (id: string) => {
  const qc = useQueryClient();
  const portal = usePortalCode();
  return useMutation({
    mutationFn: (body: { action: string; reason?: string }) =>
      api.post<unknown>(`/orders/${id}/transition`, body),
    onSuccess: () => settleWrite(qc, orderDetailWrite(portal, id)),
  });
};

/** A-14 — one status for several orders. The result names the orders that moved and those that did not. */
export interface BulkStatusResult {
  status: string;
  succeeded: Array<{ id: string; reference: string }>;
  failed: Array<{ id: string; reference: string; reason: string }>;
}
export const useBulkOrderStatus = () => {
  const qc = useQueryClient();
  const scoped = useScope();
  return useMutation({
    mutationFn: (body: { orderIds: string[]; status: string; reason?: string | undefined }) =>
      api.post<BulkStatusResult>('/orders/bulk-status', body),
    // Re-read the queue (and any open order) even when some orders failed: the ones that moved have changed.
    // Through `invalidateAll`, so this is capped too: the promise returned from `onSettled` holds
    // `isPending` exactly as `onSuccess` does, over the same broad `['portal', p, 'orders']` prefix
    // that hung A-15's buttons. Same shape, same cure.
    onSettled: () => invalidateAll(qc, [scoped(['orders'])]),
  });
};

/** A-15 — line changes during review. Only the fields given change. */
export interface LineRevision {
  lineNumber: number;
  agreedUnitPrice?: string;
  quantity?: number;
  size?: string | null;
  remark?: string | null;
}

export const useReviseOrderLines = (id: string) => {
  const qc = useQueryClient();
  const scoped = useScope();
  return useMutation({
    mutationFn: (lines: LineRevision[]) => api.post<unknown>(`/orders/${id}/reprice`, { lines }),
    onSuccess: () => invalidateAll(qc, [scoped(['orders']), scoped(['orders', id])]),
  });
};

export const useAnswerQuery = (orderId: string) => {
  const qc = useQueryClient();
  const scoped = useScope();
  return useMutation({
    mutationFn: ({ queryId, body }: { queryId: string; body: string }) =>
      api.post<unknown>(`/orders/${orderId}/queries/${queryId}/answer`, { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: scoped(['orders', orderId]) }),
  });
};

// ----------------------------------------------------------------- payments
//
// Deliberately order-scoped, not the full invoice-allocation model — see
// `db/schema/payment.ts`'s own comment.

export const useOrderPayments = (orderId: string | undefined) =>
  useQuery({
    queryKey: ['shop', 'orders', orderId, 'payments'],
    queryFn: () => api.get<PaymentProof[]>(`/shop/orders/${orderId}/payments`),
    enabled: !!orderId,
  });

export const useSubmitPaymentProof = (orderId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      amount: string; currency: string; paidOn: string; bankReference?: string | undefined;
      /** Milestone payments: the invoice this payment is for; omit for order-level money. */
      consignmentInvoiceId?: string | undefined;
    }) =>
      api.post<PaymentProof>(`/shop/orders/${orderId}/payments`, body),
    // The milestone figures are derived from these proofs, so they refresh with them.
    onSuccess: () => invalidateAll(qc, [
      ['shop', 'orders', orderId, 'payments'],
      ['shop', 'orders', orderId, 'milestones'],
    ]),
  });
};

/** The courier charge of one consignment invoice, paid by the retailer (verified by Admin like any proof). */
export const useSubmitCourierPayment = (invoiceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { amount: string; currency: string; paidOn: string; bankReference?: string | undefined }) =>
      api.post<PaymentProof>(`/shop/consignment-invoices/${invoiceId}/courier-payments`, body),
    onSuccess: () => invalidateAll(qc, [
      ['shop', 'consignment-invoices', invoiceId],
      ['shop', 'consignment-invoices'],
    ]),
  });
};

/**
 * CR-56 P4 — "I have paid this purchase invoice". Records a CLAIM: the API writes a PENDING proof and
 * nothing else, so the invoice, the payable and "available to order" are exactly as they were until
 * Admin verifies it (BG-06). Only R-27's own reads are refreshed — and there is nothing else that
 * could have changed.
 */
export const useSubmitPurchaseInvoicePayment = (invoiceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { amount: string; currency: string; paidOn: string; bankReference?: string | undefined }) =>
      api.post<PaymentProof>(`/shop/payables/invoices/${invoiceId}/payments`, body),
    onSuccess: () => invalidateAll(qc, [['shop', 'payables']]),
  });
};

/**
 * CR-56 P4 — A-45: Admin verifies or rejects a retailer's claim on a purchase invoice. Verifying is what
 * moves the payable, the invoice and the budget, so the desk, the invoice and the queue all re-read.
 */
export const useDecidePurchaseInvoiceProof = () => {
  const qc = useQueryClient();
  const scoped = useScope();
  return useMutation({
    mutationFn: ({ proofId, decision, reason }: { proofId: string; decision: 'verify' | 'reject'; reason?: string }) =>
      api.post<unknown>(`/payments/${proofId}/${decision}`, reason ? { reason } : undefined),
    onSuccess: () => invalidateAll(qc, [['payables'], scoped(['payments'])]),
  });
};

// ----------------------------------------------------------------- CR-57 / D105 — R-18's own KYC pack

/**
 * Where the retailer's own KYC pack stands. Only the Owner may read it (`shop.profile`), so a caller
 * without that passes `enabled: false` rather than collect a 403 on every visit to R-18.
 */
export const useKycPack = (enabled: boolean) =>
  useQuery({
    queryKey: ['shop', 'kyc'],
    queryFn: () => api.get<KycPack>('/shop/kyc-documents'),
    enabled,
  });

/**
 * File one document. The bytes go as the request body, the way every upload here does, and the
 * server decides what the file is from the bytes — the header and the name are not evidence.
 *
 * Filing can change the account's KYC status (a REJECTED account goes back to PENDING), and `me`
 * carries that status, so it is re-read too: otherwise R-18 would keep telling the retailer they
 * were rejected after they had already sent what was asked for.
 */
export const useFileKycDocument = () => {
  const qc = useQueryClient();
  const { refreshMe } = useSession();
  return useMutation({
    mutationFn: async (file: File) => {
      const portal = getPortal();
      const res = await fetch(`/api/shop/kyc-documents?${new URLSearchParams({ name: file.name })}`, {
        method: 'POST',
        headers: { 'content-type': file.type || 'application/octet-stream', ...(portal ? { 'x-portal': portal } : {}) },
        credentials: 'same-origin',
        body: file,
      });
      if (!res.ok) throw await uploadFailure(res);
      return res.json() as Promise<{ kycStatus: string; remaining: number }>;
    },
    onSuccess: async () => {
      await invalidateAll(qc, [['shop', 'kyc']]);
      await refreshMe();
    },
  });
};

/** Remove one — how a replace is done. A document that is not this account's is a 404. */
export const useRemoveKycDocument = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => api.del<unknown>(`/shop/kyc-documents/${documentId}`),
    onSuccess: () => invalidateAll(qc, [['shop', 'kyc']]),
  });
};

/** The file is a separate call, same reason `catalog.ts`'s image upload is:
 *  raw bytes as the body, not JSON. */
export const uploadPaymentProofFile = (proofId: string, file: File) =>
  fetch(`/api/shop/payments/${proofId}/file`, {
    method: 'POST',
    headers: { 'content-type': file.type || 'application/octet-stream' },
    credentials: 'same-origin',
    body: file,
  });

export const useOrderPaymentsAdmin = (orderId: string | undefined) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['orders', orderId, 'payments']),
    queryFn: () => api.get<PaymentProof[]>(`/orders/${orderId}/payments`),
    enabled: !!orderId,
  });
};

export const useVerifyPayment = (orderId: string) => {
  const qc = useQueryClient();
  const scoped = useScope();
  return useMutation({
    mutationFn: ({ proofId, decision, reason }: { proofId: string; decision: 'verify' | 'reject'; reason?: string }) =>
      api.post<unknown>(`/payments/${proofId}/${decision}`, reason ? { reason } : undefined),
    // Verifying or rejecting moves the milestone figures and may unlock an order action.
    onSuccess: () => invalidateAll(qc, [
      scoped(['orders', orderId, 'payments']),
      scoped(['orders', orderId, 'milestones']),
      scoped(['orders', orderId]),
      // A courier-charge proof lives on the invoice's anchor order but is shown on the invoice.
      scoped(['consignment-invoices']),
    ]),
  });
};

// -------------------------------------------------------- milestone payments
//
// 30/30/30/10 (docs/plans/milestone-payments). The figures are derived by the API from verified
// proofs; these hooks only read them. `scheduled: false` is a legacy order and renders nothing new.

export const useOrderMilestones = (orderId: string | undefined) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['orders', orderId, 'milestones']),
    queryFn: () => api.get<OrderMilestones>(`/orders/${orderId}/milestones`),
    enabled: !!orderId,
  });
};

export const useShopOrderMilestones = (orderId: string | undefined) =>
  useQuery({
    queryKey: ['shop', 'orders', orderId, 'milestones'],
    queryFn: () => api.get<OrderMilestones>(`/shop/orders/${orderId}/milestones`),
    enabled: !!orderId,
  });

/** Admin: attribute a PENDING proof to a consignment invoice, or clear it (null). */
export const useAttributeProof = (orderId: string) => {
  const qc = useQueryClient();
  const scoped = useScope();
  return useMutation({
    mutationFn: ({ proofId, ...body }: { proofId: string } & AttributeProof) =>
      api.post<unknown>(`/payments/${proofId}/attribute`, body),
    onSuccess: () => invalidateAll(qc, [
      scoped(['orders', orderId, 'milestones']),
      scoped(['orders', orderId, 'payments']),
    ]),
  });
};

/** Admin: refund a CANCELLED order's full verified amount (a new reversing ledger entry). */
export const useRecordRefund = (orderId: string) => {
  const qc = useQueryClient();
  const scoped = useScope();
  return useMutation({
    mutationFn: (body: RecordRefund) => api.post<RefundResult>(`/orders/${orderId}/refund`, body),
    onSuccess: () => invalidateAll(qc, [
      scoped(['orders', orderId, 'milestones']),
      scoped(['orders', orderId, 'payments']),
    ]),
  });
};

// ---------------------------------------------------------------- shipments
//
// SH-01 through SH-11 — A-18. A consignment against one or more READY orders;
// dispatch/deliver cascade to `OrderService.transition()` server-side, so
// invalidating `orders` here is what keeps an order's status current after
// the shipment carrying it moves.

export const useShipments = () => {
  const scoped = useScope();
  return useQuery({ queryKey: scoped(['shipments']), queryFn: () => api.get<Shipment[]>('/shipments') });
};

export const useShipment = (id: string | undefined) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['shipments', id]),
    queryFn: () => api.get<ShipmentDetail>(`/shipments/${id}`),
    enabled: !!id,
  });
};

export const usePackingList = (id: string | undefined) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['shipments', id, 'packing-list']),
    queryFn: () => api.get<PackingList>(`/shipments/${id}/packing-list`),
    enabled: !!id,
  });
};

export const useOrderShipments = (orderId: string | undefined) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['orders', orderId, 'shipments']),
    queryFn: () => api.get<Shipment[]>(`/orders/${orderId}/shipments`),
    enabled: !!orderId,
  });
};

/** One hook for pack/dispatch/deliver/cancel — same shape, different verb.
 *
 *  Always sends a JSON object, for the reason `useSubmitOrder` gives: with no
 *  reason, a bodiless POST reaches `zShipmentAction` as `undefined` and fails
 *  "Validation failed" at the root, even though every field is optional.
 *  `{ reason: undefined }` serialises to `{}`, which the schema accepts. */
/*
 * Both of these are driven only from A-19's shipment panel, so both take the INVOICE they are
 * inside as well as the shipment. A shipment has no screen of its own — it is drawn from the
 * invoice's payload — so the invoice is what must be re-read before the button comes back, and
 * the rest of what the write touches is re-read without being waited for. See `write-keys.ts`.
 */

/** SH-02 — correct a shipment's courier, courier charge or tracking number. Never changes its
 *  state. The charge is money: a decimal string (NF-04), `null` to clear it, absent to leave it. */
export const useUpdateShipmentDetails = (id: string, invoiceId?: string) => {
  const qc = useQueryClient();
  const portal = usePortalCode();
  return useMutation({
    mutationFn: (
      body: { carrier?: string | null; trackingReference?: string | null },
    ) => api.patch<unknown>(`/shipments/${id}`, body),
    onSuccess: () => settleWrite(qc, consignmentInvoiceWrite(portal, invoiceId)),
  });
};

export const useShipmentAction = (id: string, invoiceId?: string) => {
  const qc = useQueryClient();
  const portal = usePortalCode();
  return useMutation({
    mutationFn: (
      { action, reason }: { action: 'dispatch' | 'cancel'; reason?: string | undefined },
    ) => api.post<Shipment>(`/shipments/${id}/${action}`, { reason: reason || undefined }),
    // Awaited, not fired and forgotten: `mutateAsync` resolves once the invoice carrying this
    // shipment has been re-read, so the panel shows the persisted state the moment the action
    // ends instead of the old one for a beat. Cancelling reopens the invoice, which is the same
    // query, so one re-read answers for both.
    onSuccess: () => settleWrite(qc, consignmentInvoiceWrite(portal, invoiceId)),
  });
};

// -------------------------------------------------------- consignment invoices
//
// docs/plans/consignment-invoice-parent.
// D97: the DESPATCH document. The invoice is the parent: orders, quantities,
// terms and charges live on it, and the shipment is created from it. A mutation
// that changes an invoice can change its orders' claimable quantity and the
// shipment, so each one invalidates all three prefixes.

export const useConsignmentInvoices = () => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['consignment-invoices']),
    queryFn: () => api.get<ConsignmentInvoiceRow[]>('/consignment-invoices'),
  });
};

export const useConsignmentInvoice = (id: string | undefined) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['consignment-invoices', id]),
    queryFn: () => api.get<ConsignmentInvoiceDetail>(`/consignment-invoices/${id}`),
    enabled: !!id,
  });
};

/** READY orders with their unclaimed lines, optionally for one company (the create page). */
export const useConsignmentInvoiceCandidates = (retailerId?: string) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['consignment-invoices', 'candidates', retailerId ?? 'all']),
    queryFn: () => api.get<ConsignmentInvoiceCandidate[]>(
      `/consignment-invoices/candidates${retailerId ? `?retailerId=${encodeURIComponent(retailerId)}` : ''}`,
    ),
  });
};

/** For the order page: the invoices an order is on. */
export const useOrderConsignmentInvoices = (orderId: string | undefined) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['orders', orderId, 'consignment-invoices']),
    queryFn: () => api.get<ConsignmentInvoiceRow[]>(`/orders/${orderId}/consignment-invoices`),
    enabled: !!orderId,
  });
};

export interface CreateConsignmentInvoiceBody {
  orderIds: string[];
  lines?: Array<{ orderLineId: string; quantity: number }> | undefined;
}

/**
 * Every one of these awaits only A-19's own invoice and invalidates the rest in the background.
 * Awaiting all three prefixes is what left "Create consignment invoice" and "Create shipment" greyed
 * out over writes the database had already taken — `write-keys.ts` has the whole account.
 */
export const useCreateConsignmentInvoice = () => {
  const qc = useQueryClient();
  const portal = usePortalCode();
  return useMutation({
    mutationFn: (body: CreateConsignmentInvoiceBody) => api.post<ConsignmentInvoiceRow>('/consignment-invoices', body),
    onSuccess: () => settleWrite(qc, consignmentInvoiceCreateWrite(portal)),
  });
};

export const useUpdateConsignmentInvoiceLines = (id: string) => {
  const qc = useQueryClient();
  const portal = usePortalCode();
  return useMutation({
    mutationFn: (lines: Array<{ orderLineId: string; quantity: number }>) =>
      api.put<ConsignmentInvoiceDetail>(`/consignment-invoices/${id}/lines`, { lines }),
    onSuccess: () => settleWrite(qc, consignmentInvoiceWrite(portal, id)),
  });
};

export const useCancelConsignmentInvoice = (id: string) => {
  const qc = useQueryClient();
  const portal = usePortalCode();
  return useMutation({
    mutationFn: (reason: string) => api.post<ConsignmentInvoiceRow>(`/consignment-invoices/${id}/cancel`, { reason }),
    onSuccess: () => settleWrite(qc, consignmentInvoiceWrite(portal, id)),
  });
};

export interface CreateShipmentFromInvoiceBody {
  courierName: string;
  // No courier charge: Miraki does not charge a consignment retailer for the courier (04-Nov-2026).
  trackingNumber?: string | undefined;
}

export const useCreateShipmentFromInvoice = (id: string) => {
  const qc = useQueryClient();
  const portal = usePortalCode();
  return useMutation({
    mutationFn: (body: CreateShipmentFromInvoiceBody) =>
      api.post<Shipment>(`/consignment-invoices/${id}/shipment`, body),
    onSuccess: () => settleWrite(qc, consignmentInvoiceWrite(portal, id)),
  });
};

// --------------------------------------------- retailer: consignment invoices (read-only)
//
// What Miraki raised for this account's orders, and the shipment created from it. Polled while the
// page is open and visible, so a status Miraki changes shows up without a reload; the server is the
// only source of every figure and status.

const CONSIGNMENT_INVOICE_POLL_MS = 30_000;

/** R-23 — all of this account's consignment invoices. Polled like the per-order list: a shipment moves on without the retailer. */
export const useShopConsignmentInvoices = () =>
  useQuery({
    queryKey: ['shop', 'consignment-invoices'],
    queryFn: () => api.get<ShopConsignmentInvoiceRow[]>('/shop/consignment-invoices'),
    refetchInterval: CONSIGNMENT_INVOICE_POLL_MS,
  });

export const useShopOrderConsignmentInvoices = (orderId: string | undefined) =>
  useQuery({
    queryKey: ['shop', 'orders', orderId, 'consignment-invoices'],
    queryFn: () => api.get<ShopConsignmentInvoiceRow[]>(`/shop/orders/${orderId}/consignment-invoices`),
    enabled: !!orderId,
    refetchInterval: CONSIGNMENT_INVOICE_POLL_MS,
  });

/**
 * A-45 — one PURCHASE invoice (CR-56). Keyed under `['payables']`, the prefix A-40's writes already
 * invalidate, so a payment confirmed on the desk re-reads the invoice it was applied to.
 */
export const usePurchaseInvoice = (id: string | undefined) => {
  const { portal } = useSession();
  return useQuery({
    queryKey: ['payables', 'invoice', id, portal?.code ?? null],
    queryFn: () => api.get<PurchaseInvoiceDetail>(`/payables/invoices/${id}`),
    enabled: !!id,
  });
};

/** R-27 — the retailer's own purchase invoice. Under `['shop', 'payables']`, the prefix R-22 reads. */
export const useShopPurchaseInvoice = (id: string | undefined) =>
  useQuery({
    queryKey: ['shop', 'payables', 'invoice', id],
    queryFn: () => api.get<PurchaseInvoiceDetail>(`/shop/payables/invoices/${id}`),
    enabled: !!id,
  });

export const useShopConsignmentInvoice = (id: string | undefined) =>
  useQuery({
    queryKey: ['shop', 'consignment-invoices', id],
    queryFn: () => api.get<ShopConsignmentInvoice>(`/shop/consignment-invoices/${id}`),
    enabled: !!id,
    refetchInterval: CONSIGNMENT_INVOICE_POLL_MS,
  });

/** SH-10..SH-12 — the per-piece tally. Same poll as the invoice: a sale reported elsewhere changes it. */
export const useDeliveryReconciliation = (id: string | undefined) =>
  useQuery({
    queryKey: ['shop', 'consignment-invoices', id, 'reconciliation'],
    queryFn: () => api.get<Reconciliation>(`/shop/consignment-invoices/${id}/reconciliation`),
    enabled: !!id,
    refetchInterval: CONSIGNMENT_INVOICE_POLL_MS,
  });

// ---------------------------------------------------------------- change requests

/** Quantity/size change requests on one order. `scope` is whose door it is read through. */
export const useOrderChanges = (scope: 'shop' | 'admin', orderId: string | undefined) =>
  useQuery({
    queryKey: ['order-changes', scope, orderId],
    queryFn: () => api.get<OrderChanges>(scope === 'shop' ? `/shop/orders/${orderId}/change-requests` : `/orders/${orderId}/change-requests`),
    enabled: !!orderId,
  });

export const useRequestOrderChange = (orderId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lines: Array<{ lineId: string; quantity?: number; size?: string | null }>) =>
      api.post<{ id: string; status: 'PENDING' }>(`/shop/orders/${orderId}/change-requests`, { lines }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['order-changes'] }),
  });
};

export const useDecideOrderChange = (orderId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { requestId: string; decision: 'approve' | 'reject'; reason?: string }) =>
      api.post(`/orders/${orderId}/change-requests/${v.requestId}/decision`, { decision: v.decision, reason: v.reason }),
    // An approval changes the order and its total, so everything that shows either is re-read.
    // (Admin keys carry the portal in front of them, so match on the key's parts, not its start.)
    onSettled: () => qc.invalidateQueries({
      predicate: (q) => q.queryKey.includes('orders') || q.queryKey.includes('order-changes')
        || q.queryKey.includes('pending-approvals'),
    }),
  });
};

export const useMyAddressRequests = () =>
  useQuery({ queryKey: ['address-requests', 'mine'], queryFn: () => api.get<AddressRequestRow[]>('/shop/account/address-requests') });

export const useRequestAddressChange = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<{ id: string }>('/shop/account/address-requests', body),
    onSettled: () => qc.invalidateQueries({ queryKey: ['address-requests'] }),
  });
};

/** R-18 — the account's own team, for an Owner to manage. */
export interface TeamMember {
  id: string; firstName: string | null; lastName: string | null; name: string; email: string; phone: string | null;
  isActive: boolean; isPrimary: boolean; isYou: boolean; lastSignInAt: string | null; locked: boolean;
  roles: Array<{ id: string; name: string }>;
}
export interface TeamData { members: TeamMember[]; roles: Array<{ id: string; name: string }> }

export const useTeam = (enabled: boolean) =>
  useQuery({ queryKey: ['shop', 'team'], queryFn: () => api.get<TeamData>('/shop/team'), enabled });

/** Every team change re-reads the list and the account page's read-only team block. */
const useTeamMutation = <V, R>(fn: (v: V) => Promise<R>) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => invalidateAll(qc, [['shop', 'team'], ['shop', 'account']]),
  });
};

export interface TeamForm { firstName: string; lastName: string; phone: string | null; roleId: string }
export const useAddTeamMember = () =>
  useTeamMutation((b: TeamForm & { email: string }) => api.post<{ id: string; email: string; temporaryPassword: string }>('/shop/team', b));
export const useEditTeamMember = () =>
  useTeamMutation(({ id, ...b }: TeamForm & { id: string }) => api.put<{ id: string }>(`/shop/team/${id}`, b));
export const useTeamMemberAction = () =>
  useTeamMutation(({ id, action }: { id: string; action: 'deactivate' | 'reactivate' | 'reset-password' }) =>
    api.post<{ id: string; temporaryPassword?: string }>(`/shop/team/${id}/${action}`, {}));

export const useAddressRequests = (retailerId: string | undefined) =>
  useQuery({
    queryKey: ['address-requests', retailerId],
    queryFn: () => api.get<AddressRequestRow[]>(`/retailers/${retailerId}/address-requests`),
    enabled: !!retailerId,
  });

export const useDecideAddressRequest = (retailerId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { requestId: string; decision: 'approve' | 'reject'; reason?: string }) =>
      api.post(`/retailers/${retailerId}/address-requests/${v.requestId}/decision`, { decision: v.decision, reason: v.reason }),
    onSettled: () => qc.invalidateQueries({
      predicate: (q) => q.queryKey.includes('address-requests') || q.queryKey.includes('retailer')
        || q.queryKey.includes('account') || q.queryKey.includes('pending-approvals'),
    }),
  });
};

/* ------------------------------------------------------------------------------------
 * D97 Wave 0 — the old names, kept alive for one wave only.
 *
 * The HTTP path segment and the react-query cache key are the SAME literal here, so both
 * moved to 'consignment-invoices' in one commit — a half-rename of that string does not fail
 * a typecheck, it silently stops invalidating and the UI shows a stale invoice after a write.
 * The API controllers answer the new paths from WS-A/WS-B onwards, in this same release.
 *
 * Wave 1's four sessions each migrate their own call sites against a green typecheck; none of
 * them may edit this file. **Wave 3 deletes everything below this line, in the same release**,
 * and the vocabulary gate enforces it — these names are allow-listed as transitional entries,
 * and an allow-list entry that matches nothing is itself a gate failure (exit 2).
 * ----------------------------------------------------------------------------------- */

